import React, { useContext } from 'react';

import {
	ADMIN_HOME_ID,
	COMPANY_HUB_SETTINGS_ID,
} from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { getCompanyHubSideNav } from '@confluence/breadcrumbs/entry-points/getCompanyHubSideNav.ts';
import { CompanyHomeBuilderUpsellPage } from '@confluence/change-edition/entry-points/CompanyHomeBuilderUpsellPage.ts';
import {
	CompanyHubSettingsPage,
	CompanyHubSettingsPageTabsType,
} from '@confluence/company-hub/entry-points/CompanyHubSettingsPage.ts';
import { useCompanyHubPremiumGate } from '@confluence/company-hub-utils/entry-points/useCompanyHubPremiumGate.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { getCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { getCompanyHubPseudoAppRoute } from '@confluence/ssr-app/entry-points/getCompanyHubPseudoAppRoute.ts';

import { SpacePermissionsSettingsRoute } from './SpacePermissionsSettingsRoute.tsx';
import { SpaceMembersRoute } from './SpaceMembersRoute.tsx';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';

/**
 * The routes "Company Hub settings > Users" and "Hub permissions" in the Confluence app.
 * `AdminCompanyHubPermissionsRoute` wraps this one to implement the Company Hub pseudo app.
 */
function ConfluenceAppHubPermissionsRoute({ params, ...props }: RouteMatch) {
	const shouldShowPremiumUpsell = useCompanyHubPremiumGate();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { isRbacEnabled } = useSpaceRoleMode();

	if (isSiteAdmin && shouldShowPremiumUpsell) {
		return <CompanyHomeBuilderUpsellPage featureName="companyHubPermissions" />;
	}

	if (!params.tab) {
		return <CompanyHubSettingsPage tab={CompanyHubSettingsPageTabsType.USERS} />;
	}

	const spaceKey = getCompanyHubSpaceKey();
	if (isRbacEnabled) {
		return <SpaceMembersRoute {...props} params={{ spaceKey, ...params }} />;
	}

	return <SpacePermissionsSettingsRoute {...props} params={{ spaceKey, ...params }} />;
}

ConfluenceAppHubPermissionsRoute.NAVIGATION_PARAMS = (routeMatch: RouteMatch) => {
	const { name, params } = routeMatch;
	const spaceKey = getCompanyHubSpaceKey();
	const navView = getCompanyHubSideNav(routeMatch) ? ADMIN_HOME_ID : COMPANY_HUB_SETTINGS_ID;

	return {
		Screen: {
			screenEvent: {
				name: `${params.tab || 'consolidated'}HubPermissionsScreen`,
				id: spaceKey,
			},
			pageState: { spaceKey, routeName: name },
		},
		MainLayout: {
			navView,
			spaceKey,
		},
	};
};

/**
 * The routes "Company Hub settings > Users" and "Hub permissions" in the Company Hub pseudo app if enabled; otherwise, in the Confluence app.
 */
export const AdminCompanyHubPermissionsRoute = getCompanyHubPseudoAppRoute(
	ConfluenceAppHubPermissionsRoute,
);
